/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Tilted (3D) boat frames for a pitched map.
 *
 * Each boat layer has a top-view layer and a tilted layer on the same source.
 * Tilted frames show whenever the camera is pitched, however it got there
 * (3D mode, mouse or touch, active trip follow); otherwise the top view
 * shows. The frame is camera-relative (heading minus map bearing), which
 * expressions cannot read, so it is written to each feature as `tiltedImage`
 * and refreshed when the map rotates.
 */
import { telemetry } from '../../backend';
import { setDataSource } from '../_helpers';
import { ensureBoatTiltedImages } from './boat-icon-images';
import { boatIconSize, normalizeHeading } from './boat-icons';

// The frames were drawn for a 75° camera (the 3D mode pitch); below 40° they
// read wrong. They show at every zoom while pitched.
export const TILTED_BOAT_MIN_PITCH = 40;

const FRAME_COUNT = 16;
const FRAME_STEP = 360 / FRAME_COUNT;

// Matches nothing. Switching views by filter leaves layer visibility to the
// existing layer toggles. (['==', 1, 0] is read as an invalid legacy filter.)
const NO_FEATURES = ['boolean', false];

/** Frame `00` to `15` for a heading as seen from the camera. */
export const boatFrame = (heading: unknown, bearing: number): string => {
  const relative = (((normalizeHeading(heading) - bearing) % 360) + 360) % 360;
  const frame = Math.round(relative / FRAME_STEP) % FRAME_COUNT;
  return frame < 10 ? `0${frame}` : `${frame}`;
};

const isTopBoatIcon = (image: unknown): image is string =>
  typeof image === 'string' && /^boat-[a-z]+-top$/.test(image);

/** Upright, camera-facing frame. Same size ramp and waterline anchor as the top view. */
export const tiltedBoatIconLayout = {
  'icon-image': ['to-string', ['get', 'tiltedImage']],
  'icon-size': boatIconSize,
  'icon-anchor': 'center',
  'icon-pitch-alignment': 'viewport',
  'icon-rotation-alignment': 'viewport',
  'icon-allow-overlap': true,
  'text-allow-overlap': true,
};

export interface BoatLayerPair {
  sourceId: string;
  topLayerId: string;
  tiltedLayerId: string;
  /** Filter both layers share, e.g. boats only on a mixed source */
  filter?: any[];
}

const layerPairs: { [sourceId: string]: BoatLayerPair } = {};
const latestData: { [sourceId: string]: any } = {};
const writtenFrames: { [sourceId: string]: string } = {};

let mapReference: any = null;
let tilted = false;
// Weak, so maps from unmounted map components can be garbage collected
const watchedMaps = new WeakSet<object>();

let renderChain: Promise<void> = Promise.resolve();
let frameRequested = false;
let forceNextRender = false;

export const isTiltedBoatView = (): boolean => tilted;

const shouldTilt = (map: any): boolean =>
  map.getPitch() >= TILTED_BOAT_MIN_PITCH;

const topIconsIn = (data: any): string[] => {
  const icons: string[] = [];
  (data?.features || []).forEach((feature: any) => {
    const image = feature?.properties?.image;
    if (isTopBoatIcon(image) && icons.indexOf(image) === -1) icons.push(image);
  });
  return icons;
};

const withTiltedImages = (data: any, bearing: number) => ({
  ...data,
  features: (data?.features || []).map((feature: any) => {
    const image = feature?.properties?.image;
    if (!isTopBoatIcon(image)) return feature;
    return {
      ...feature,
      properties: {
        ...feature.properties,
        tiltedImage: image.replace(
          /-top$/,
          `-${boatFrame(feature.properties.heading, bearing)}`,
        ),
      },
    };
  }),
});

const applyFilters = (map: any, pair: BoatLayerPair) => {
  const shared = pair.filter || null;
  if (map.getLayer(pair.topLayerId)) {
    map.setFilter(pair.topLayerId, tilted ? NO_FEATURES : shared);
  }
  if (map.getLayer(pair.tiltedLayerId)) {
    map.setFilter(pair.tiltedLayerId, tilted ? shared : NO_FEATURES);
  }
};

// Skips setData when no boat changed frame, so rotating stays cheap
const writeSource = (map: any, sourceId: string, force: boolean) => {
  const data = latestData[sourceId];
  if (!data) return;
  const framed = withTiltedImages(data, map.getBearing());
  const frames = framed.features
    .map((feature: any) => feature?.properties?.tiltedImage || '')
    .join(',');
  if (!force && frames === writtenFrames[sourceId]) return;
  writtenFrames[sourceId] = frames;
  setDataSource(map, sourceId, framed);
};

const showTopView = (map: any) => {
  tilted = false;
  Object.keys(layerPairs).forEach((sourceId) => {
    applyFilters(map, layerPairs[sourceId]);
  });
};

const loadTiltedImages = async (map: any, icons: string[]) => {
  try {
    await ensureBoatTiltedImages(map, icons);
    return true;
  } catch (error) {
    telemetry(
      telemetry.WARN,
      `[tiltedBoats] Failed to load tilted boat icons, ${error}`,
    );
    return false;
  }
};

const render = async (force: boolean) => {
  const map = mapReference;
  if (!map) return;
  let next = shouldTilt(map);
  if (!next && !tilted && !force) return;

  if (next) {
    const icons: string[] = [];
    Object.keys(latestData).forEach((sourceId) => {
      topIconsIn(latestData[sourceId]).forEach((icon) => {
        if (icons.indexOf(icon) === -1) icons.push(icon);
      });
    });
    // Load before switching, so the tilted layer never shows missing images.
    // If loading fails, stay on the top view.
    next = (await loadTiltedImages(map, icons)) && shouldTilt(map);
  }

  const modeChanged = next !== tilted;
  tilted = next;
  Object.keys(layerPairs).forEach((sourceId) => {
    if (tilted) writeSource(map, sourceId, modeChanged || force);
    if (modeChanged || force) applyFilters(map, layerPairs[sourceId]);
  });
};

const nextFrame = (callback: () => void) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(callback);
  } else {
    setTimeout(callback, 16);
  }
};

// Log and carry on, so one failed update does not stop later ones
const logUpdateError = (error: unknown) => {
  telemetry(
    telemetry.WARN,
    `[tiltedBoats] Failed to update the boat view, ${error}`,
  );
};

// At most one render per animation frame, run in order
const scheduleRender = (force = false) => {
  forceNextRender = forceNextRender || force;
  if (frameRequested) return;
  frameRequested = true;
  nextFrame(() => {
    frameRequested = false;
    const forced = forceNextRender;
    forceNextRender = false;
    renderChain = renderChain.then(() => render(forced)).catch(logUpdateError);
  });
};

/** Resolves once scheduled view updates have run. */
export const tiltedBoatViewSettled = (): Promise<void> =>
  new Promise<void>((resolve) => {
    nextFrame(resolve);
  }).then(() => renderChain);

/**
 * Follow pitch and bearing for this map. Safe to call from every
 * onInitialize; it re-applies the view after a base map change.
 */
export const watchTiltedBoatView = (map: any): void => {
  mapReference = map;
  if (!watchedMaps.has(map)) {
    watchedMaps.add(map);
    map.on('rotate', () => scheduleRender());
    map.on('pitch', () => scheduleRender());
  }
  scheduleRender(true);
};

/**
 * Pair a top-view layer with its tilted layer. Call when the layers are
 * added. The top view shows until the next render confirms the pitch and has
 * loaded the frames.
 */
export const registerBoatLayers = (map: any, pair: BoatLayerPair): void => {
  layerPairs[pair.sourceId] = pair;
  const wasTilted = tilted;
  tilted = false;
  applyFilters(map, pair);
  tilted = wasTilted;
  scheduleRender(true);
};

/** Set a boat source's data, with each boat's tilted frame for the current bearing. */
export const setBoatSourceData = (
  map: any,
  sourceId: string,
  data: any,
): Promise<void> => {
  latestData[sourceId] = data;
  // Queued behind any render, so a switch to tilted that is still loading
  // frames cannot miss boat types in this data
  renderChain = renderChain
    .then(async () => {
      // New boat types need their frames before the tilted layer can draw
      // them. If loading fails, fall back to the top view.
      if (
        tilted &&
        !(await loadTiltedImages(map, topIconsIn(latestData[sourceId])))
      ) {
        showTopView(map);
      }
      writeSource(map, sourceId, true);
    })
    .catch(logUpdateError);
  return renderChain;
};
